// InitiativeContext — the shared scoping provider (Synterra Integration Sprint,
// item A1).
//
// Every internal screen used to re-implement project scoping on its own:
// `useActiveInitiative` for the active initiative, `useAuthIdentity` +
// `allowedPanels` for role/permissions, `useVerticals` for the vertical
// registry, plus ad-hoc per-dashboard state for the active site and each
// screen inventing its own loading / empty / permission states.
//
// This provider composes all of those into ONE context so CEDAR, LEAF, BIOME,
// TERRA, EPIC, cost capture, governance, and reviewer dashboards can read the
// active initiative, active site, active vertical, user role + permissions, and
// a consolidated loading/error state from a single source.
//
// SECURITY NOTE: this is UX/clarity plumbing only. The backend (`require_admin`,
// initiative/site quarantine, role-based access) remains the sole authorization
// boundary and is not weakened here. All requests still flow through
// `adminFetch`, which carries the staff session cookie + `X-Initiative-Id` /
// `?initiative_id=` scoping exactly as before.
//
// It deliberately COMPOSES the existing hooks (`useActiveInitiative`,
// `useAuthIdentity`, `useVerticals`) rather than replacing them, and it reuses
// the same `localStorage` key (`spartis.activeInitiativeId`) and change-events
// (`spartis:activeInitiativeChanged`, identity-changed) so unmigrated screens
// stay in sync and nothing that still reads them breaks.
import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { hasAdminCode } from '../config';
import { apiFetch } from '../api/client';
import {
  useActiveInitiative,
  Initiative,
} from '../hooks/useActiveInitiative';
import { useAuthIdentity, allowedPanels, AuthIdentity, AuthAssignment } from '../auth/identity';
import { useVerticals, VerticalRecord } from '../hooks/useVerticals';
import { usePitchMode } from '../hooks/usePitchMode';

/** A site available within the active initiative (from `/api/site-profiles/`). */
export interface InitiativeSite {
  id?: number;
  site_id?: string;
  site_name?: string;
  name?: string;
  [key: string]: unknown;
}

function siteKey(s: InitiativeSite): string {
  return String(s.site_id ?? s.id ?? '');
}

function siteName(s: InitiativeSite): string {
  return (s.site_name || s.name || siteKey(s) || 'Unnamed site').toString();
}

/**
 * The consolidated scoping state a screen can render distinct UI for:
 * - `loading`               — still resolving identity / initiatives.
 * - `permission-denied`     — either (a) identity resolved but the caller lacks
 *                             internal (staff / break-glass / demo) access, or
 *                             (b) a signed-in staff member operating under their
 *                             own role whose account isn't assigned to the
 *                             currently-selected initiative (the backend returns
 *                             404 for every scoped call in that case).
 * - `empty`                 — has access but no initiatives are available.
 * - `no-initiative-selected`— a multi-initiative user who hasn't picked one yet.
 * - `ready`                 — everything resolved and an initiative is in scope
 *                             (or a single-initiative user auto-scoped).
 */
export type InitiativeScopeStatus =
  | 'loading'
  | 'permission-denied'
  | 'empty'
  | 'no-initiative-selected'
  | 'ready';

export interface InitiativeContextValue {
  // ── Active initiative ──────────────────────────────────────────────
  initiatives: Initiative[];
  activeInitiativeId: string | null;
  activeInitiative: Initiative | null;
  setActiveInitiative: (id: string | number | null) => void;
  initiativeName: (i: Initiative) => string;

  // ── Active site (scoped to the active initiative) ──────────────────
  sites: InitiativeSite[];
  activeSiteId: string | null;
  activeSite: InitiativeSite | null;
  setActiveSite: (id: string | null) => void;
  siteName: (s: InitiativeSite) => string;

  // ── Active vertical (derived from the active initiative) ───────────
  verticals: VerticalRecord[];
  activeVertical: VerticalRecord | null;

  // ── Signed-in user role + permissions ──────────────────────────────
  identity: AuthIdentity | null;
  isStaff: boolean;
  isReadOnly: boolean;
  assignments: AuthAssignment[];
  /** Allowed nav-panel indices, or `null` for "no restriction" (see identity). */
  allowedPanelSet: Set<number> | null;
  /** True when the viewer has internal access (staff, break-glass, or demo). */
  hasInternalAccess: boolean;

  // ── Consolidated state ─────────────────────────────────────────────
  loading: boolean;
  /** Narrow flag: just the initiatives list is still loading. */
  initiativesLoading: boolean;
  error: string | null;
  status: InitiativeScopeStatus;

  /** Re-fetch every underlying source (initiatives, identity, verticals, sites). */
  refresh: () => Promise<void>;
}

const InitiativeContext = createContext<InitiativeContextValue | null>(null);

export function InitiativeProvider({ children }: { children: React.ReactNode }) {
  const {
    initiatives,
    activeId: activeInitiativeId,
    active: activeInitiative,
    setActive: setActiveInitiative,
    loading: initiativesLoading,
    pickName: initiativeName,
    refresh: refreshInitiatives,
  } = useActiveInitiative();

  const { identity, loading: identityLoading, refresh: refreshIdentity } = useAuthIdentity();
  const { verticals, loading: verticalsLoading, error: verticalsError, reload: reloadVerticals } = useVerticals();
  const pitchMode = usePitchMode();

  // Break-glass admin code, tracked reactively (mirrors App.tsx).
  const [adminUnlocked, setAdminUnlocked] = useState<boolean>(hasAdminCode);
  useEffect(() => {
    const sync = () => setAdminUnlocked(hasAdminCode());
    window.addEventListener('spartis:adminCodeChanged', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('spartis:adminCodeChanged', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // ── Sites for the active initiative ──────────────────────────────────
  const [sites, setSites] = useState<InitiativeSite[]>([]);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [activeSiteId, setActiveSiteIdState] = useState<string | null>(null);

  const loadSites = useCallback(async () => {
    if (!activeInitiativeId) {
      setSites([]);
      return;
    }
    setSitesLoading(true);
    setSitesError(null);
    try {
      // adminFetch injects `?initiative_id=` from the shared localStorage key,
      // but we pass it explicitly too so the fetch always tracks the active
      // initiative even if state briefly diverges.
      const res = await apiFetch(`/api/site-profiles/?initiative_id=${encodeURIComponent(activeInitiativeId)}`, { context: 'admin' });
      if (!res.ok) {
        setSites([]);
        // A 401/403/404 here is a scoping/permission outcome, not a hard error
        // the whole screen should surface — keep it quiet.
        return;
      }
      const data = await res.json();
      const list: InitiativeSite[] = Array.isArray(data)
        ? data
        : (data.items || data.sites || []);
      setSites(list);
    } catch {
      setSites([]);
      setSitesError('Could not load sites for this initiative.');
    } finally {
      setSitesLoading(false);
    }
  }, [activeInitiativeId]);

  useEffect(() => {
    loadSites();
  }, [loadSites]);

  // Clear the selected site whenever the initiative changes so a stale site
  // from another initiative can never leak into a new scope.
  useEffect(() => {
    setActiveSiteIdState(null);
  }, [activeInitiativeId]);

  const setActiveSite = useCallback((id: string | null) => {
    setActiveSiteIdState(id === null || id === '' ? null : String(id));
  }, []);

  const activeSite = useMemo(
    () => sites.find((s) => siteKey(s) === activeSiteId) || null,
    [sites, activeSiteId],
  );

  // ── Active vertical (derived from the active initiative) ─────────────
  const activeVertical = useMemo(() => {
    const key = activeInitiative?.intervention_vertical;
    if (!key) return null;
    return verticals.find((v) => v.key === key) || null;
  }, [activeInitiative, verticals]);

  // ── Role + permissions ───────────────────────────────────────────────
  const isStaff = !!identity?.is_staff;
  const isReadOnly = !!identity?.is_read_only;
  const assignments = useMemo(() => identity?.assignments ?? [], [identity]);
  const allowedPanelSet = useMemo(
    () => allowedPanels(identity, { hasAdminCode: adminUnlocked, pitchMode }),
    [identity, adminUnlocked, pitchMode],
  );
  const hasInternalAccess = isStaff || adminUnlocked || pitchMode;

  // ── Consolidated loading / error / status ────────────────────────────
  const loading = identityLoading || initiativesLoading || verticalsLoading || sitesLoading;
  const error = verticalsError || sitesError || null;

  // A signed-in staff member operating under their OWN role is only authorized
  // for initiatives they hold a UserAssignment to. Break-glass admin, pitch/demo
  // mode, and the `internal_admin` role all span every initiative, so they are
  // never denied here. When a non-spanning staff member has an active initiative
  // that isn't one of their assignments, the backend returns 404 for every
  // scoped call — surface that distinctly so the shell can explain it instead of
  // rendering generic empty panels. Mirrors the backend
  // `_authorize_internal_session` rule (replit.md §3, role-based access).
  const deniedForActiveInitiative = useMemo(() => {
    if (adminUnlocked || pitchMode) return false;
    if (!isStaff) return false;
    if (identity?.role === 'internal_admin') return false;
    if (!activeInitiativeId) return false;
    return !assignments.some(
      (a) => String(a.project_config_id) === activeInitiativeId,
    );
  }, [adminUnlocked, pitchMode, isStaff, identity, activeInitiativeId, assignments]);

  const status: InitiativeScopeStatus = useMemo(() => {
    if (identityLoading || initiativesLoading) return 'loading';
    if (!hasInternalAccess) return 'permission-denied';
    if (initiatives.length === 0) return 'empty';
    // useActiveInitiative auto-scopes a lone initiative; only a genuine
    // multi-initiative user with nothing chosen is "no-initiative-selected".
    if (!activeInitiativeId && initiatives.length > 1) return 'no-initiative-selected';
    // Signed-in staff whose role doesn't cover the active initiative.
    if (deniedForActiveInitiative) return 'permission-denied';
    return 'ready';
  }, [identityLoading, initiativesLoading, hasInternalAccess, initiatives.length, activeInitiativeId, deniedForActiveInitiative]);

  const refresh = useCallback(async () => {
    await Promise.all([
      refreshInitiatives(),
      refreshIdentity(),
      reloadVerticals(),
      loadSites(),
    ]);
  }, [refreshInitiatives, refreshIdentity, reloadVerticals, loadSites]);

  const value: InitiativeContextValue = {
    initiatives,
    activeInitiativeId,
    activeInitiative,
    setActiveInitiative,
    initiativeName,

    sites,
    activeSiteId,
    activeSite,
    setActiveSite,
    siteName,

    verticals,
    activeVertical,

    identity,
    isStaff,
    isReadOnly,
    assignments,
    allowedPanelSet,
    hasInternalAccess,

    loading,
    initiativesLoading,
    error,
    status,

    refresh,
  };

  return (
    <InitiativeContext.Provider value={value}>
      {children}
    </InitiativeContext.Provider>
  );
}

/**
 * Read the shared scoping context. Throws a clear error if used outside the
 * provider so a mis-mounted screen fails loudly instead of silently getting
 * default/undefined scope.
 */
export function useInitiativeContext(): InitiativeContextValue {
  const ctx = useContext(InitiativeContext);
  if (ctx === null) {
    throw new Error('useInitiativeContext must be used within an <InitiativeProvider>');
  }
  return ctx;
}
